import React from 'react';
import { Auth0IDTokenProvider, WaysteClient, WaysteProvider } from '@alliance-disposal/client';
import { StandardAuth0Provider } from '@wayste/auth-ui-shell';
import { ErrorBoundary } from '@wayste/shadcn/components/error-boundary';
import { init as initApm } from '@elastic/apm-rum';
import '@fontsource-variable/roboto';
import '@fontsource-variable/roboto/wght-italic.css';
import { Loader, LoaderOptions } from '@googlemaps/js-api-loader';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import './index.css';
import { router } from './structure/router';
import { initDatadogRum } from './utils/datadog-rum';
import { initUserflow } from './utils/userflow';

if (import.meta.env.MODE !== 'localhost') {
    initDatadogRum();

    initApm({
        // Set required service name (allowed characters: a-z, A-Z, 0-9, -, _, and space)
        serviceName: __APP_NAME__.replace('@wayste/', ''),

        // Set custom APM Server URL (default: http://localhost:8200)
        serverUrl: 'https://1c478fe7e83a416987ea30a190f8d3a1.apm.us-east-1.aws.cloud.es.io:443',

        // Set service version (required for sourcemap feature)
        serviceVersion: __APP_VERSION__,

        // Set Environment
        environment: import.meta.env.MODE === 'production' ? 'production' : 'staging',

        // Set distributed tracing origin for APM Server
        distributedTracingOrigins: import.meta.env.MODE === 'production' ? ['https://api.wayste.com'] : ['https://api-staging.wayste.com'],

        // Set global labels
        logLevel: import.meta.env.MODE === 'production' ? 'warn' : 'warn',

        breakdownMetrics: true,
    });
}

const googleLibraries: LoaderOptions['libraries'] = ['places', 'core', 'geocoding'];

const loader = new Loader({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    version: 'weekly',
    libraries: googleLibraries,
    id: 'google-maps-script',
});

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            retry: 2,
        },
    },
});

googleLibraries.map((library) => loader.importLibrary(library).catch(console.error));
initUserflow();

// Initialized eagerly at module scope (not inside PostHogProvider's effect) so it's
// guaranteed to be loaded before App's auth-subscription effect calls posthog.identify() —
// React fires child effects before parent effects, so an apiKey-driven PostHogProvider init
// can otherwise lose the identify() call to an unloaded client, leaving the session anonymous
// for PostHog flag targeting for the rest of its lifetime.
posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: 'https://us.i.posthog.com',
    capture_pageview: false,
    capture_exceptions: true,
});

const waysteClient = new WaysteClient({
    API: {
        baseApiUrl: import.meta.env.VITE_BASE_API_URL,
    },
    settings: {
        defaultApp: 'wayste',
        defaultRole: 'HAULER_ADMIN',
    },
    idTokenProvider: new Auth0IDTokenProvider(),
});

createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
                <PostHogProvider client={posthog}>
                    <StandardAuth0Provider
                        config={{
                            domain: import.meta.env.VITE_AUTH0_DOMAIN,
                            clientID: import.meta.env.VITE_AUTH0_CLIENT_ID,
                            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
                            organization: import.meta.env.VITE_AUTH0_ORGANIZATION,
                            environmentName: import.meta.env.MODE,
                        }}
                    >
                        <WaysteProvider client={waysteClient}>
                            <NuqsAdapter>
                                <RouterProvider router={router} />
                            </NuqsAdapter>
                        </WaysteProvider>
                    </StandardAuth0Provider>
                </PostHogProvider>
            </QueryClientProvider>
        </ErrorBoundary>
    </React.StrictMode>,
);
